// NotificationPreferences — the Profile "Notifications" section. Holds one
// control today: the 08:00 digest of orders placed by this user's reports.
//
// Rendered on both Profile pages (engineer and office) so a manager finds the
// switch wherever they sign in. Returns null for anyone who can't receive the
// digest — only approvers, admins and procurement who manage a team can, so
// for engineers and anyone without a team a switch would be noise.
//
// Requires an ancestor <Theme> provider for Radix Themes tokens to resolve,
// which both host pages already establish.

import { useState, type CSSProperties } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, Flex, Heading, Switch, Text } from '@radix-ui/themes'

import { useMe } from '../../auth/hooks/useMe'
import { updateProfile } from '../../auth/services/auth-api'
import { useToast } from '../radix'

const SAVE_FAILED_MESSAGE =
  'Could not save your notification setting. Please try again.'

// Radix fills a switch's "on" track from --accent-track, which neither host
// page's accent overrides set (so it fell back to Radix blue / grey). Point it
// at the tenant brand colour so "on" reads in the brand, on both pages.
const BRAND_SWITCH_STYLE = { '--accent-track': 'var(--brand-primary)' } as CSSProperties

/** Pull the backend's error message out of an API rejection, if it sent one. */
function getSaveErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return SAVE_FAILED_MESSAGE
  }
  const { data } = error as { data: Record<string, unknown> }
  return typeof data?.error === 'string' ? data.error : SAVE_FAILED_MESSAGE
}

export function NotificationPreferences() {
  const { data: meData } = useMe()
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  // Holds the value being saved so the switch moves the instant it is
  // flipped rather than waiting on the round trip. Cleared once the cached
  // account details are refreshed — or on failure, which snaps the switch
  // back to whatever the server still holds.
  const [pendingValue, setPendingValue] = useState<boolean | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const canReceiveDigest = meData?.can_receive_daily_digest ?? false
  // Default off, matching the server: the digest ships disabled and a manager
  // receives it only after switching it on. A missing value must never show
  // "on" for an email that is not being sent.
  const savedValue = meData?.daily_order_digest_enabled ?? false
  const isEnabled = pendingValue ?? savedValue

  if (!canReceiveDigest) {
    return null
  }

  const handleChange = async (nextValue: boolean): Promise<void> => {
    setPendingValue(nextValue)
    setIsSaving(true)
    try {
      await updateProfile({ daily_order_digest_enabled: nextValue })
      await queryClient.invalidateQueries({ queryKey: ['auth-me'] })
      // Naming the new state, not just "saved": the switch has already moved,
      // so the useful confirmation is which way it landed.
      addToast(
        nextValue
          ? 'Daily order digest turned on'
          : 'Daily order digest turned off',
        'success',
        2000,
      )
    } catch (error) {
      addToast(getSaveErrorMessage(error), 'error')
    } finally {
      setPendingValue(null)
      setIsSaving(false)
    }
  }

  // The card lives inside this component rather than in each host page so
  // that a user who can't receive the digest renders nothing at all, not an empty
  // card on two pages.
  return (
    <Card size={{ initial: '2', sm: '3' }}>
      <Heading size={{ initial: '3', sm: '4' }} mb="1">
        Notifications
      </Heading>
      <Text size="2" color="gray" mb={{ initial: '3', sm: '4' }} as="p">
        Choose which emails you receive.
      </Text>

      <Flex justify="between" align="center" gap="3">
        <Flex direction="column" gap="1">
          <Text size="2" weight="medium">
            Daily order digest
          </Text>
          <Text size="1" color="gray">
            A summary each morning of the orders your team placed the day
            before.
          </Text>
        </Flex>
        <Switch
          style={BRAND_SWITCH_STYLE}
          checked={isEnabled}
          disabled={isSaving}
          onCheckedChange={handleChange}
          aria-label="Daily order digest"
        />
      </Flex>
    </Card>
  )
}
